import { useState } from 'react'
import { ChevronDown, ChevronUp, Fish, Scale, Bug, Image as ImageIcon, Loader2, X } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import type { FishHealthCheck } from '@/api/fishHealthApi'
import { imageApi } from '@/shared/api/imageApi'
import { queryKeys } from '@/shared/api/queryClient'

const riskColors = {
  low: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  critical: 'bg-red-100 text-red-800 border-red-200',
}

interface HealthCheckReferenceCardProps {
  check: FishHealthCheck
  pondName?: string
  onRemove?: () => void
  disabled?: boolean
}

export function HealthCheckReferenceCard({
  check,
  pondName,
  onRemove,
  disabled = false,
}: HealthCheckReferenceCardProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  // Fetch signed URLs for images when expanded
  const { data: signedUrlsResponse, isLoading: isImagesLoading } = useQuery({
    queryKey: queryKeys.images.signedUrls(check.imageIds || []),
    queryFn: () => imageApi.getSignedUrls(check.imageIds || []),
    enabled: isExpanded && !!check.imageIds && check.imageIds.length > 0,
  })

  const images = signedUrlsResponse?.images || []
  const hasImages = check.imageIds && check.imageIds.length > 0

  // Calculate mortality rate if not provided
  const mortalityRate = check.mortalityRate ??
    (check.mortalityCount && check.sampleSize > 0
      ? ((check.mortalityCount / check.sampleSize) * 100)
      : 0)

  return (
    <div className="border border-slate-200/50 rounded-lg bg-white overflow-hidden shadow-sm">
      {/* Collapsed Header */}
      <div
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="p-2 bg-blue-50 rounded-lg border border-blue-200/50">
          <Fish className="h-4 w-4 text-blue-600" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900 truncate">
              {pondName || check.pond?.name || `Pond ${check.pondId.slice(0, 8)}`}
            </span>
            {check.riskLevel && (
              <span className={`px-2 py-0.5 text-xs font-medium rounded border ${riskColors[check.riskLevel]}`}>
                {check.riskLevel.toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
            <span>{format(new Date(check.checkedAt), 'MMM d, yyyy • h:mm a')}</span>
            {check.healthScore !== undefined && (
              <span className="flex items-center gap-1">
                <Scale className="h-3 w-3" />
                Score: {check.healthScore.toFixed(0)}
              </span>
            )}
            {hasImages && (
              <span className="flex items-center gap-1">
                <ImageIcon className="h-3 w-3" />
                {check.imageIds?.length} photos
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onRemove && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onRemove()
              }}
              disabled={disabled}
              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              title="Remove check"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-slate-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-slate-400" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-slate-200/50 p-4 space-y-4 bg-slate-50/50">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-2.5 bg-white rounded-lg border border-slate-200/50">
              <p className="text-xs text-slate-500">Avg Weight</p>
              <p className="font-semibold text-slate-900">{check.averageWeight.toFixed(1)}g</p>
            </div>
            <div className="p-2.5 bg-white rounded-lg border border-slate-200/50">
              <p className="text-xs text-slate-500">Sample Size</p>
              <p className="font-semibold text-slate-900">{check.sampleSize} fish</p>
            </div>
            {check.mortalityCount !== undefined && (
              <div className="p-2.5 bg-white rounded-lg border border-slate-200/50">
                <p className="text-xs text-slate-500">Mortality</p>
                <p className="font-semibold text-slate-900">{check.mortalityCount} ({mortalityRate.toFixed(1)}%)</p>
              </div>
            )}
            {check.feedConversionRatio !== undefined && (
              <div className="p-2.5 bg-white rounded-lg border border-slate-200/50">
                <p className="text-xs text-slate-500">FCR</p>
                <p className="font-semibold text-slate-900">{check.feedConversionRatio.toFixed(2)}</p>
              </div>
            )}
          </div>

          {/* Parasite Detection */}
          {check.parasiteCode && check.parasiteName && (
            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200/50 rounded-lg">
              <Bug className="h-5 w-5 text-amber-600" />
              <div>
                <p className="text-sm font-medium text-amber-900">Parasite Detected</p>
                <p className="text-xs text-amber-700">{check.parasiteCode} - {check.parasiteName}</p>
              </div>
            </div>
          )}

          {/* Observations */}
          {(check.physicalSymptoms || check.behaviorObservations || check.observations) && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Observations</p>
              {check.physicalSymptoms && (
                <div className="p-2.5 bg-white rounded-lg border border-slate-200/50">
                  <p className="text-xs text-slate-500 mb-1">Physical Symptoms</p>
                  <p className="text-sm text-slate-700">{check.physicalSymptoms}</p>
                </div>
              )}
              {check.behaviorObservations && (
                <div className="p-2.5 bg-white rounded-lg border border-slate-200/50">
                  <p className="text-xs text-slate-500 mb-1">Behavior</p>
                  <p className="text-sm text-slate-700">{check.behaviorObservations}</p>
                </div>
              )}
              {check.observations && (
                <div className="p-2.5 bg-white rounded-lg border border-slate-200/50">
                  <p className="text-xs text-slate-500 mb-1">General</p>
                  <p className="text-sm text-slate-700">{check.observations}</p>
                </div>
              )}
            </div>
          )}

          {/* Treatment */}
          {check.treatmentApplied && (
            <div className="p-2.5 bg-emerald-50 border border-emerald-200/50 rounded-lg">
              <p className="text-xs text-emerald-600 mb-1">Treatment Applied</p>
              <p className="text-sm text-emerald-800">{check.treatmentApplied}</p>
            </div>
          )}

          {/* Images */}
          {hasImages && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Photos ({check.imageIds?.length})
              </p>
              {isImagesLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />
                </div>
              ) : images.length > 0 ? (
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {images.map((image) => (
                    <div
                      key={image.imageId}
                      className="aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200/50"
                    >
                      <img
                        src={image.signedUrl}
                        alt={image.fileName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">Images could not be loaded</p>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="flex items-center gap-4 text-xs text-slate-400 pt-2 border-t border-slate-200/50">
            <span>Checked: {format(new Date(check.checkedAt), 'PPpp')}</span>
            {check.createdByName && <span>By: {check.createdByName}</span>}
          </div>
        </div>
      )}
    </div>
  )
}

export default HealthCheckReferenceCard
